import { LoadingOutlined } from "@ant-design/icons";
import { Col, Input, Row, Select } from "antd";
import { useEffect, FC, useState } from "react";

import {
  generateArrayOfYearsForSalaryRange,
  showSuccessPopup,
  sortedByName,
} from "@utils";

import {
  ICountry,
  IJobGrade,
  ISalaryRange,
  IModal,
} from "@types";

import {
  ErrorServices,
  useCreateSalaryRangeMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useUpdateSalaryRangeMutation,
} from "@/services";

import {
  useFormValidation,
  useTypedSelector,
} from "@hooks";

import CustomModal from "@/components/customModel";

import "./index.css";

import { formatNumberWithCommas } from "@/utils/formatNumberWithCommas";

const { Option } = Select;

interface IAddSalaryRange extends IModal {
  selectedSalaryRange: ISalaryRange | null;

  setSelectedSalaryRange: React.Dispatch<
    React.SetStateAction<ISalaryRange | null>
  >;
}

const INITIAL_SALARY_RANGE = {
  grade_id: null,
  year: null,
  tier: null,
  country_id: null,
  city: null,
  range_type: "",
  salary_min: null,
  salary_mid: null,
  salary_max: null,
};

const AddSalaryRange: FC<IAddSalaryRange> = ({
  isVisible,
  setIsVisible,
  selectedSalaryRange,
  setSelectedSalaryRange,
}) => {
  const { company } = useTypedSelector(
    state => state.auth?.user!
  );

  const { id: company_id } = company!;

  const [salaryRange, setSalaryRange] =
    useState<ISalaryRange>(
      INITIAL_SALARY_RANGE
    );

  const [gradeCountries, setGradeCountries] =
    useState<ICountry[]>([]);

  const [selectedGrade, setSelectedGrade] =
    useState<IJobGrade | null>(null);

  const [
    createSalaryRange,
    { isLoading: isCreating },
  ] = useCreateSalaryRangeMutation();

  const [
    updateSalaryRange,
    { isLoading: isUpdating },
  ] = useUpdateSalaryRangeMutation();

  const validation =
    useFormValidation(
      salaryRange,
      v => ({
        grade_id: !v.grade_id,

        country: !v.country_id,

        range_type: !v.range_type,

        salary_min: !v.salary_min,

        salary_mid: !v.salary_mid,

        salary_max: !v.salary_max,
      })
    );

  const {
    data: jobGradesData,
    isLoading: isFetchingGrades,
  } =
    useFetchAllCompanyJobGradesQuery({
      company_id,
    });

  const { data: jobGrades } =
    jobGradesData || {};

  const { data: countriesData } =
    useFetchCompanyCountriesQuery({
      company_id,
    });

  const { data: countries } =
    countriesData || {};

  useEffect(() => {
    if (selectedSalaryRange) {
      const clonedSalaryRange = {
        ...selectedSalaryRange,
      };

      delete clonedSalaryRange.country;
      delete clonedSalaryRange.id;

      // FIXED
      delete clonedSalaryRange.grade_obj;

      // FIXED
      const selectedGradeObject =
        jobGrades?.find(
          (grade: IJobGrade) =>
            grade.id ===
            selectedSalaryRange?.grade_obj
              ?.id
        ) || null;

      setSelectedGrade(
        selectedGradeObject
      );

      setSalaryRange({
        ...clonedSalaryRange,

        // FIXED
        grade_id:
          selectedSalaryRange
            ?.grade_obj?.id ||
          null,

        salary_max:
          clonedSalaryRange?.salary_max?.replace?.(
            /,/g,
            ""
          ) ||
          clonedSalaryRange?.salary_max,

        salary_mid:
          clonedSalaryRange?.salary_mid?.replace?.(
            /,/g,
            ""
          ) ||
          clonedSalaryRange?.salary_mid,

        salary_min:
          clonedSalaryRange?.salary_min?.replace?.(
            /,/g,
            ""
          ) ||
          clonedSalaryRange?.salary_min,

        country_id:
          selectedSalaryRange
            ?.country?.id,
      });

      setGradeCountries(
        selectedGradeObject
          ?.countries?.length
          ? selectedGradeObject?.countries
          : countries
      );
    }

    return () => {
      setSelectedSalaryRange(null);

      setSelectedGrade(null);

      setSalaryRange(
        INITIAL_SALARY_RANGE
      );
    };
  }, [
    selectedSalaryRange,
    setSelectedSalaryRange,
    jobGrades,
    countries,
  ]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const cleanedValue =
      event.target.value.replace(
        /,/g,
        ""
      );

    setSalaryRange(prev => ({
      ...prev,
      [event.target.name]:
        cleanedValue,
    }));
  };

  const handleSelectChange = (
    label: string,
    value: number
  ) => {
    setSalaryRange(prev => ({
      ...prev,
      [label]: value,
    }));
  };

  const handleSubmit =
    async () => {
      try {
        if (
          selectedSalaryRange
        ) {
          await editSalaryRange();
        } else {
          await addSalaryRange();
        }

        setIsVisible(false);

        showSuccessPopup({
          title:
            selectedSalaryRange
              ? "Salary Range Updated"
              : "New Salary Range Created",

          desc: `You have successfully ${
            selectedSalaryRange
              ? "updated the"
              : "created new"
          } salary range.`,

          role: "client",
        });
      } catch (error) {
        ErrorServices(error);
      }
    };

  const addSalaryRange =
    async () =>
      await createSalaryRange({
        company_id,

        body: {
          ...salaryRange,
        },
      }).unwrap();

  const editSalaryRange =
    async () =>
      await updateSalaryRange({
        company_id,

        id: selectedSalaryRange?.id,

        body: {
          ...salaryRange,
        },
      }).unwrap();

  return (
    <CustomModal
      width="w-8/12"
      isVisible={isVisible}
      title={`${
        selectedSalaryRange
          ? "Update"
          : "Create"
      } Salary Range`}
      onClose={() =>
        setIsVisible(false)
      }
      footer={
        <>
          <button
            className="bg-themeGrey text-themeBlack font-medium text-lg py-2 px-14 rounded-md"
            onClick={() =>
              setIsVisible(false)
            }
          >
            Cancel
          </button>

          <button
            className={`bg-themeBlue text-white font-medium text-lg py-2 px-14 rounded-md ${
              !validation.isValid &&
              "opacity-50"
            } `}
            disabled={
              !validation.isValid ||
              isCreating ||
              isUpdating
            }
            onClick={handleSubmit}
          >
            {isCreating ||
            isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : selectedSalaryRange ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Grade
            </label>

            <Select
              className="custom-select"
              loading={
                isFetchingGrades
              }
              size="large"
              showArrow
              placeholder="Select grade from here..."
              showSearch={false}
              value={
                salaryRange?.grade_id ||
                undefined
              }
              onBlur={() =>
                validation.touch(
                  "grade_id"
                )
              }
              optionLabelProp="label"
              onChange={(
                gradeId: number
              ) => {
                const selectedGradeObject =
                  jobGrades?.find(
                    (
                      grade: IJobGrade
                    ) =>
                      grade.id ===
                      gradeId
                  );

                setSelectedGrade(
                  selectedGradeObject
                );

                setSalaryRange(
                  prev => ({
                    ...prev,
                    grade_id:
                      gradeId,
                    country_id:
                      null,
                  })
                );

                setGradeCountries(
                  selectedGradeObject
                    ?.countries
                    ?.length
                    ? selectedGradeObject?.countries
                    : countries
                );
              }}
            >
              {jobGrades?.map(
                (
                  jobGrade: IJobGrade
                ) => (
                  <Option
                    label={`${jobGrade.grade}`}
                    key={
                      jobGrade.id
                    }
                    value={
                      jobGrade.id
                    }
                  >
                    {
                      jobGrade.grade
                    }
                  </Option>
                )
              )}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Country
            </label>

            <Select
              className="custom-select"
              disabled={
                !salaryRange?.grade_id
              }
              size="large"
              showArrow
              placeholder="Select country from here..."
              showSearch={false}
              value={
                salaryRange?.country_id ||
                undefined
              }
              onBlur={() =>
                validation.touch(
                  "country"
                )
              }
              onChange={country_id =>
                setSalaryRange(
                  (
                    prev: any
                  ) => ({
                    ...prev,
                    country_id,
                  })
                )
              }
            >
              {sortedByName(
                gradeCountries
              )?.map(
                (
                  country: ICountry
                ) => (
                  <Option
                    key={
                      country?.id
                    }
                    value={
                      country?.id
                    }
                  >
                    {
                      country?.name
                    }
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              City
            </label>

            <Input
              className="w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
              onChange={
                handleChange
              }
              size="large"
              name="city"
              value={
                salaryRange?.city ||
                null
              }
              placeholder="Enter city here..."
            />
          </Col>

          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Tier
            </label>

            <Input
              className="w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
              size="large"
              type="number"
              name="tier"
              onChange={
                handleChange
              }
              value={
                salaryRange?.tier ||
                ""
              }
              placeholder="Enter tier here..."
            />
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Range Name
            </label>

            <Input
              className="w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
              size="large"
              name="range_type"
              onBlur={() =>
                validation.touch(
                  "range_type"
                )
              }
              onChange={
                handleChange
              }
              value={
                salaryRange?.range_type
              }
              placeholder="Enter range name here..."
            />
          </Col>

          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Year
            </label>

            <Select
              className="custom-select"
              size="large"
              showArrow
              placeholder="Select year from here..."
              showSearch={false}
              value={
                salaryRange?.year ||
                undefined
              }
              onChange={val =>
                handleSelectChange(
                  "year",
                  val
                )
              }
            >
              {generateArrayOfYearsForSalaryRange(
                2000,
                2030
              )?.map(year => (
                <Option
                  key={year}
                  value={year}
                >
                  {year}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between">
          <Col span={8}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Min
            </label>

            <Input
              className="w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
              size="large"
              type="text"
              name="salary_min"
              onBlur={() =>
                validation.touch(
                  "salary_min"
                )
              }
              onChange={
                handleChange
              }
              placeholder="Enter min here..."
              value={formatNumberWithCommas(
                salaryRange?.salary_min ||
                  ""
              )}
            />
          </Col>

          <Col span={7}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Mid
            </label>

            <Input
              className="w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
              size="large"
              type="text"
              name="salary_mid"
              onBlur={() =>
                validation.touch(
                  "salary_mid"
                )
              }
              onChange={
                handleChange
              }
              placeholder="Enter mid here..."
              value={formatNumberWithCommas(
                salaryRange?.salary_mid ||
                  ""
              )}
            />
          </Col>

          <Col span={8}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Max
            </label>

            <Input
              className="w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md"
              size="large"
              type="text"
              name="salary_max"
              onBlur={() =>
                validation.touch(
                  "salary_max"
                )
              }
              onChange={
                handleChange
              }
              placeholder="Enter max here..."
              value={formatNumberWithCommas(
                salaryRange?.salary_max ||
                  ""
              )}
            />
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddSalaryRange;